import React from 'react';
import { Box, Typography, Container, Grid, Skeleton, Link } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useInstitutions } from '../../../hooks/useInstitutions';

/**
 * Intenta convertir cualquier URL de Google Maps a formato embebible.
 * - URLs que ya contienen /embed o output=embed se usan directamente.
 * - URLs de Google Maps normales se convierten añadiendo output=embed.
 * - Cualquier otra URL devuelve null (se mostrará como link externo).
 */
const toEmbedUrl = (url) => {
  if (!url) return null;
  if (url.includes('google.com/maps/embed')) return url;
  if (url.includes('output=embed')) return url;
  if (url.includes('google.com/maps') || url.includes('maps.google.com')) {
    const clean = url.replace(/([?&])output=[^&]*/g, (_, sep) => sep === '?' ? '?' : '');
    const sep = clean.includes('?') ? '&' : '?';
    return `${clean}${sep}output=embed`;
  }
  return null;
};

const LandingStats = () => {
  const theme = useTheme();
  const { institutions, isLoading } = useInstitutions();
  const inst = institutions?.[0] ?? null;

  const contactItems = [
    { icon: <LocationOnOutlinedIcon />, label: 'Dirección',  value: inst?.direction, href: null },
    { icon: <LocalPhoneOutlinedIcon />, label: 'Teléfono',   value: inst?.telephone ? String(inst.telephone) : null, href: inst?.telephone ? `tel:${inst.telephone}` : null },
    { icon: <EmailOutlinedIcon />,      label: 'Correo',     value: inst?.email, href: inst?.email ? `mailto:${inst.email}` : null },
    { icon: <AccessTimeOutlinedIcon />, label: 'Horario',    value: inst?.businessHours, href: null },
  ];

  return (
    <Box id="contact" sx={{ py: { xs: 10, md: 16 }, bgcolor: '#FFFFFF', borderTop: '1px solid', borderColor: 'divider' }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 6, md: 10 }}>

          {/* Heading + Map */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 120 } }}>
              <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 2.5, fontSize: '0.7rem' }}>
                CONTACTO
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900, mt: 1.5,
                  color: 'text.primary',
                  fontSize: { xs: '1.9rem', md: '2.5rem' },
                  lineHeight: 1.15, letterSpacing: '-0.02em',
                }}
              >
                Estamos aquí para atenderte
              </Typography>
              <Box sx={{ mt: 3, width: 40, height: 3, borderRadius: 2, bgcolor: 'primary.main' }} />

              {inst?.map && (() => {
                const embedUrl = toEmbedUrl(inst.map);
                return embedUrl ? (
                  /* Mapa embebido */
                  <Box sx={{
                    mt: 4,
                    borderRadius: '14px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: 260,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
                  }}>
                    <Box
                      component="iframe"
                      src={embedUrl}
                      title="Ubicación de la institución"
                      width="100%"
                      height="100%"
                      style={{ border: 0, display: 'block' }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </Box>
                ) : (
                  /* Fallback: link externo si la URL no es de Google Maps */
                  <Link
                    href={inst.map}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      mt: 4,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      color: 'primary.main',
                      fontWeight: 600,
                      fontSize: '0.88rem',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    <LocationOnOutlinedIcon sx={{ fontSize: 18 }} />
                    Ver ubicación
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Link>
                );
              })()}

              {inst?.taxId && (
                <Box sx={{
                  mt: inst?.map ? 2.5 : 5,
                  display: 'inline-flex', alignItems: 'center', gap: 1,
                  px: 2.5, py: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderRadius: '8px',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>RUC / NIT:</Typography>
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.75rem' }}>{inst.taxId}</Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Contact cards */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {contactItems.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex', gap: 3, alignItems: 'flex-start',
                    p: { xs: 3, md: 3.5 },
                    borderRadius: '14px',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      bgcolor: alpha(theme.palette.primary.lighter, 0.3),
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
                    },
                  }}
                >
                  <Box sx={{
                    flexShrink: 0, width: 44, height: 44, borderRadius: '10px',
                    bgcolor: alpha(theme.palette.primary.main, 0.07),
                    color: 'primary.main',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 21 } })}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.66rem', display: 'block', mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    {isLoading ? (
                      <Skeleton variant="text" width={180} height={20} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }} />
                    ) : item.value ? (
                      item.href ? (
                        <Link
                          href={item.href}
                          target={item.external ? '_blank' : undefined}
                          rel={item.external ? 'noopener noreferrer' : undefined}
                          underline="none"
                          sx={{ fontWeight: 600, fontSize: '0.93rem', color: 'text.primary', wordBreak: 'break-word', '&:hover': { color: 'primary.main' }, transition: 'color 0.2s' }}
                        >
                          {item.value}
                        </Link>
                      ) : (
                        <Typography sx={{ fontWeight: 600, fontSize: '0.93rem', color: 'text.primary', wordBreak: 'break-word' }}>
                          {item.value}
                        </Typography>
                      )
                    ) : (
                      <Typography sx={{ fontSize: '0.88rem', color: 'text.disabled', fontStyle: 'italic' }}>No registrado</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LandingStats;
